import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  makeStyles,
  Typography
} from "@material-ui/core";
import { Settings as SettingsIcon } from "@material-ui/icons";
import { isEmpty, truncate } from "lodash";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import ReactPlayer from "react-player";
import SponsoredSession from "../assets/images/sponsored_session.png";
import { dateTimeInterval } from "../ducks/utils";
import { useCongress, useVimeoData, useYoutubeData } from "../hooks";

const useStyles = makeStyles(theme => ({
  root: {
    position: "relative"
  },
  card: {
    position: "relative",
    paddingBottom: "56.25%",
    marginBottom: theme.spacing(1),
    backgroundColor: "white"
  },
  hiddenMediaPlayer: {
    display: "none"
  },
  cardSquare: {
    position: "relative",
    paddingBottom: "100%",
    marginBottom: theme.spacing(1),
    backgroundColor: "white"
  },
  cardNoImage: {
    position: "relative",
    paddingBottom: "56.25%",
    marginBottom: theme.spacing(1)
  },
  cardSquareNoImage: {
    position: "relative",
    paddingBottom: "100%",
    marginBottom: theme.spacing(1)
  },
  cardActionArea: {
    position: "absolute",
    height: "100%",
    padding: 0
  },
  cardActionAreaNoCover: {
    position: "absolute",
    height: "100%",
    padding: 12
  },
  cardMedia: {
    height: "100%",
    backgroundColor: "white",
    backgroundSize: "cover"
  },
  cardMediaNoCover: {
    height: "100%",
    backgroundColor: "white",
    backgroundSize: "contain"
  },
  cardContent: {
    height: "100%",
    padding: theme.spacing(3)
  },
  liveMark: {
    position: "absolute",
    top: theme.spacing(1),
    left: theme.spacing(1),
    borderRadius: "4px",
    fontSize: "0.7rem",
    fontWeight: "bold",
    color: "white",
    backgroundColor: "#FF0033",
    padding: "0px 4px",
    textTransform: "uppercase"
  },
  cog: {
    position: "absolute",
    top: theme.spacing(1) / 4,
    left: theme.spacing(1) / 4,
    background: "rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    cursor: "pointer"
  },
  durationMark: {
    position: "absolute",
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    borderRadius: "4px",
    fontSize: "0.7rem",
    fontWeight: "bold",
    color: "white",
    backgroundColor: "#0A0A2D",
    padding: "0px 4px"
  },
  sponsoredSession: {
    position: "absolute",
    right: -4,
    top: -4,
    width: 40,
    height: 51,
    zIndex: 1
  }
}));

const CardItem = ({
  coverImageUrl,
  thumbUrl,
  videoUrl,
  title,
  description,
  onClick,
  isLive,
  programEvent,
  noCover,
  square,
  isEditable,
  onEdit,
  sponsored,
  showFallback,
  showDuration
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const { congress } = useCongress();

  const [dur, setDur] = useState(undefined);

  // Try to fetch data from vimeo or youtube
  const vimeoVideoData = useVimeoData(videoUrl);
  const youtubeVideoData = useYoutubeData(videoUrl);
  const { imageUrl, duration } = isEmpty(vimeoVideoData?.imageUrl)
    ? youtubeVideoData
    : vimeoVideoData;

  // Assign preview url
  const previewUrl = coverImageUrl || imageUrl || thumbUrl;
  const shouldCover = !noCover && (previewUrl || !showFallback);

  if (duration && dur === undefined) {
    setDur(duration);
  }

  const calculatedDuration = useMemo(
    () => `${`${Math.floor(dur / 60)}`.padStart(2, "0")}:${`${dur % 60}`.padStart(2, "0")}`,
    [dur]
  );

  const onReady = player => {
    setDur(player.getInternalPlayer().getDuration());
  };

  return previewUrl || showFallback ? (
    <div className={classes.root}>
      {sponsored && (
        <Avatar
          src={SponsoredSession}
          alt="Sponsored Session"
          variant="square"
          className={classes.sponsoredSession}
        />
      )}

      <Card elevation={0} className={square ? classes.cardSquare : classes.card}>
        <CardActionArea
          onClick={onClick}
          disabled={!onClick}
          className={shouldCover ? classes.cardActionArea : classes.cardActionAreaNoCover}
        >
          {videoUrl && (videoUrl.includes("youtube") || videoUrl.includes("youtu.be")) && (
            <div className={classes.hiddenMediaPlayer}>
              <ReactPlayer url={videoUrl} onReady={onReady} />
            </div>
          )}
          <CardMedia
            component="img"
            image={previewUrl || congress.cover}
            alt={title}
            title={title}
            className={shouldCover ? classes.cardMedia : classes.cardMediaNoCover}
          />
        </CardActionArea>
        {isLive && (
          <div className={classes.liveMark}>
            &#8226;&nbsp;
            {intl.formatMessage({
              id: "Shared.CardItem.Live",
              defaultMessage: "Live"
            })}
          </div>
        )}
        {isEditable && <SettingsIcon className={classes.cog} onClick={onEdit} />}
        {showDuration && dur && <div className={classes.durationMark}>{calculatedDuration}</div>}
      </Card>
      <Typography variant="body1" noWrap>
        <strong>{title}</strong>
      </Typography>
      <Typography variant="body2" noWrap>
        {description}
      </Typography>
    </div>
  ) : (
    <div className={classes.root}>
      {sponsored && (
        <Avatar
          src={SponsoredSession}
          alt="Sponsored Session"
          variant="square"
          className={classes.sponsoredSession}
        />
      )}

      <Card elevation={0} className={square ? classes.cardSquareNoImage : classes.cardNoImage}>
        <CardActionArea onClick={onClick} disabled={!onClick} className={classes.cardActionArea}>
          <CardContent className={classes.cardContent}>
            {programEvent && (
              <Typography variant="body2">
                {dateTimeInterval(programEvent.startAt, programEvent.endAt)}
              </Typography>
            )}
            {isEditable && <SettingsIcon className={classes.cog} onClick={onEdit} />}
            <Typography variant="body1">
              <strong>{truncate(title, { length: 40 })}</strong>
            </Typography>
            <Typography variant="body2">{truncate(description, { length: 80 })}</Typography>
            {programEvent && (
              <Typography variant="body2">
                {intl.formatMessage(
                  { id: "Shared.CardItem.Rooms", defaultMessage: "Room {rooms}" },
                  { rooms: programEvent.roomList.join(", ") }
                )}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </div>
  );
};

export default CardItem;
